import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { tsr } from '../../lib/api.js';
import { useChatSocket } from '../../lib/socket.js';
import { unreadTotal } from './muteLabels.js';
import { cn } from '@hrms/ui';

export function ChatNavButton() {
  const queryClient = useQueryClient();
  const socket = useChatSocket();

  const conversationsQuery = tsr.chat.listConversations.useQuery({
    queryKey: ['chat-conversations-list'],
    // New and edited messages arrive over the socket (the listeners below), so polling is only the
    // fallback for while it is down. Returning to the tab refetches, for anything read elsewhere.
    refetchInterval: socket.isConnected ? false : 60_000,
    refetchOnWindowFocus: true,
  });

  const conversations =
    conversationsQuery.data?.status === 200 ? conversationsQuery.data.body.conversations : [];

  // Muted conversations do not count toward the badge (same rule as the chat sidebar's total).
  const unreadCount = unreadTotal(conversations);

  useEffect(() => {
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: ['chat-conversations-list'] });
    };
    const offNew = socket.onMessage(refresh);
    const offUpdated = socket.onMessageUpdated(refresh);
    // Anything that arrived while the socket was down never fired the listeners above.
    const offReconnect = socket.onReconnect(refresh);
    return () => {
      offNew();
      offUpdated();
      offReconnect();
    };
  }, [socket, queryClient]);

  return (
    <NavLink
      to="/chat"
      aria-label="Chat"
      className={({ isActive }) =>
        cn(
          'relative flex size-10 shrink-0 items-center justify-center rounded-full transition-colors sm:size-9',
          isActive
            ? 'bg-accent/15 text-accent font-semibold'
            : 'bg-bg-subtle text-fg-muted hover:text-fg hover:bg-bg-subtle/80',
        )
      }
    >
      <MessageSquare className="size-4" />
      {unreadCount > 0 && (
        <span
          className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold leading-none text-white ring-2 ring-surface"
          aria-hidden="true"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
      <span className="sr-only">
        {unreadCount > 0 ? `${unreadCount} unread chat messages` : 'Chat'}
      </span>
    </NavLink>
  );
}
